/**
 * Selection math for rewriting one passage inside a plain textarea (#2671).
 * Offsets are UTF-16 indexes, the same ones selectionStart and selectionEnd use.
 */

import {
  REWRITE_PASSAGE_SHORTCUT_MAC,
  REWRITE_PASSAGE_SHORTCUT_WIN,
} from './writerCopy';

export const WHOLE_SCENE_FRACTION = 0.8;
export const TINY_WORD_COUNT = 5;
export const MAX_TAKES = 3;
export const OVERLAY_DRIFT_PX = 4;
export const UNDO_MS = 10000;
export const PHONE_MAX_PX = 767;
export const FULL_PAGE_MAX_PX = 360;
export const STACK_BELOW_PX = 1024;
export const DRAFT_MIN_BESIDE_PANEL = 720;
export const PANEL_WIDTH_PX = 400;
export const PANEL_EDGE_PX = 16;
/** Side by side needs the panel plus a readable strip of the same column. */
export const COLUMN_STACK_BELOW_PX = 720;
/**
 * A draft column this narrow cannot hold the rewrite panel. The panel
 * takes the full viewport width instead of painting inside the column.
 */
export const BLEED_BELOW_PX = 360;

export function countWords(text) {
  const parts = String(text || '').trim().split(/\s+/).filter(Boolean);
  return parts.length;
}

function isSpace(ch) {
  return ch != null && /\s/.test(ch);
}

/**
 * Same snap as the passage-rewrite route (`expand_to_word_bounds`).
 * A word is a run of non-whitespace. Punctuation stays with the word
 * it touches. Offsets that already sit on a boundary stay put.
 * The returned text replaces this span, including when the posted
 * selection stopped mid-word.
 */
export function expandToWordBounds(text, start, end) {
  const source = String(text || '');
  const n = source.length;
  let a = Math.max(0, Math.min(Number(start) || 0, n));
  let b = Math.max(a, Math.min(Number(end) || 0, n));
  while (a > 0 && a < n && !isSpace(source[a - 1]) && !isSpace(source[a])) a -= 1;
  while (b < n && b > 0 && !isSpace(source[b - 1]) && !isSpace(source[b])) b += 1;
  return { start: a, end: b, text: source.slice(a, b) };
}

export function coversMostOfScene(fullText, selectedText) {
  const full = String(fullText || '').trim();
  const selected = String(selectedText || '').trim();
  if (!full || !selected) return false;
  return selected.length / full.length >= WHOLE_SCENE_FRACTION;
}

/** Paragraphs are blocks split by a blank line. */
export function paragraphRanges(text) {
  const source = String(text || '');
  const ranges = [];
  const re = /\n[ \t]*\n/g;
  let last = 0;
  let match = re.exec(source);
  while (match) {
    ranges.push({ start: last, end: match.index });
    last = match.index + match[0].length;
    match = re.exec(source);
  }
  ranges.push({ start: last, end: source.length });
  return ranges.filter((range) => source.slice(range.start, range.end).trim());
}

/** Up to two paragraphs before the selection and one after. Not the selection's own paragraph. */
export function contextRuleRanges(text, selStart, selEnd) {
  const paras = paragraphRanges(text);
  if (!paras.length) return [];
  const start = Math.min(selStart, selEnd);
  const end = Math.max(selStart, selEnd);
  let first = -1;
  let last = -1;
  paras.forEach((para, index) => {
    const hits = end > para.start && start < para.end;
    if (!hits) return;
    if (first < 0) first = index;
    last = index;
  });
  if (first < 0) return [];
  const before = paras.slice(Math.max(0, first - 2), first);
  const after = paras.slice(last + 1, last + 2);
  return [...before, ...after];
}

export function splicePassage(full, start, end, replacement) {
  const source = String(full || '');
  const a = Math.max(0, Math.min(start, source.length));
  const b = Math.max(a, Math.min(end, source.length));
  return source.slice(0, a) + String(replacement || '') + source.slice(b);
}

function overlapIsEcho(text, overlap, edge) {
  const trimmed = String(overlap || '').trim();
  if (trimmed.length < 2) return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return true;
  if (edge === 'prefix') {
    if (/[.!?,;:]$/.test(trimmed)) return true;
    const next = text[overlap.length] || '';
    return /[.!?,;:]/.test(next);
  }
  if (/^[.!?,;:]/.test(trimmed)) return true;
  const idx = text.length - overlap.length;
  const prev = edge === 'suffix' && idx > 0 ? text[idx - 1] : '';
  return /[.!?,;:]/.test(prev);
}

function stripFollowingEcho(replacement, after) {
  const source = String(replacement || '');
  const tail = String(after || '');
  if (source.length < 2 || tail.length < 2) return source;
  const max = Math.min(source.length, tail.length);
  // Slice first, then lowercase. Some letters (İ) grow when lowercased,
  // so a length taken from the lowercased string cuts the wrong span.
  for (let len = max; len >= 2; len -= 1) {
    const raw = source.slice(-len);
    if (raw.toLowerCase() !== tail.slice(0, len).toLowerCase()) continue;
    if (!overlapIsEcho(source, raw, 'suffix')) return source;
    const rest = source.slice(0, -len).trimEnd();
    if (!rest.trim()) return source;
    return rest;
  }
  return source;
}

function stripLeadingEcho(replacement, before) {
  const source = String(replacement || '');
  const head = String(before || '').trimEnd();
  const lead = source.trimStart();
  if (lead.length < 2 || head.length < 2) return source;
  const max = Math.min(lead.length, head.length);
  // Slice first, then lowercase. Some letters grow when lowercased.
  for (let len = max; len >= 2; len -= 1) {
    const raw = lead.slice(0, len);
    if (raw.toLowerCase() !== head.slice(head.length - len).toLowerCase()) continue;
    const prev = head.length - len > 0 ? head[head.length - len - 1] : '';
    const next = len < lead.length ? lead[len] : '';
    const beforeEdge = head.length - len === 0 || isSpace(prev) || /[.!?,;:]/.test(prev);
    const afterEdge = len === lead.length || isSpace(next) || /[.!?,;:]/.test(next);
    if (!beforeEdge || !afterEdge) continue;
    if (!overlapIsEcho(lead, raw, 'prefix')) return source;
    const rest = lead.slice(len).trimStart();
    if (!rest.trim()) return source;
    return rest;
  }
  return source;
}

/**
 * The model sometimes repeats the words in front of the selection, or pastes
 * the start of the next sentence onto the reply ("…waited. and the dog").
 * Drop either echo. The caller still replaces exactly [start, end).
 */
export function cleanPassageReplacement(draft, start, end, replacement) {
  const source = String(draft || '');
  const a = Math.max(0, Math.min(Number(start) || 0, source.length));
  const b = Math.max(a, Math.min(Number(end) || 0, source.length));
  const withoutTail = stripFollowingEcho(replacement, source.slice(b));
  return stripLeadingEcho(withoutTail, source.slice(0, a));
}

/**
 * Same span the passage route rewrites: drop edge spaces, then widen to
 * whole words. Punctuation stays with the word it touches. An all-space
 * selection stays empty so it is not widened into the next word.
 */
export function resolveSelectionSpan(text, start, end) {
  const source = String(text || '');
  const n = source.length;
  let a = Math.max(0, Math.min(Number(start) || 0, n));
  let b = Math.max(a, Math.min(Number(end) || 0, n));
  while (a < b && isSpace(source[a])) a += 1;
  while (b > a && isSpace(source[b - 1])) b -= 1;
  if (a === b) return { start: a, end: b, text: '' };
  return expandToWordBounds(source, a, b);
}

/**
 * Keep one slice byte-for-byte while the rest of the draft stays editable.
 * An edit that touches the slice is dropped. An edit before it shifts the slice.
 */
export function editKeepingLockedPassage(prev, next, lock) {
  if (!lock) return { text: next, lock: null };
  const source = String(prev || '');
  const edited = String(next || '');
  const start = lock.start;
  const end = lock.end;
  if (start < 0 || end < start || end > source.length) return { text: source, lock };
  let prefix = 0;
  const limit = Math.min(source.length, edited.length);
  while (prefix < limit && source[prefix] === edited[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < source.length - prefix
    && suffix < edited.length - prefix
    && source[source.length - 1 - suffix] === edited[edited.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const prevChangeEnd = source.length - suffix;
  const nextChangeEnd = edited.length - suffix;
  const overlaps = prefix < end && prevChangeEnd > start;
  if (overlaps) return { text: source, lock: { start, end } };
  const delta = (nextChangeEnd - prefix) - (prevChangeEnd - prefix);
  const shift = prefix <= start ? delta : 0;
  return { text: edited, lock: { start: start + shift, end: end + shift } };
}

/**
 * Move a stored passage when the draft changes.
 * lockEdits drops any change that touches the passage and leaves the draft as it was.
 * Otherwise an outside edit shifts the passage, and an inside edit keeps the same words
 * when they still appear once.
 */
export function followPassage(prev, next, lock, { lockEdits = false, markChanged = false } = {}) {
  if (!lock) return { text: next, passage: null, rejected: false, changed: false };
  const kept = editKeepingLockedPassage(prev, next, lock);
  if (kept.text === next && kept.lock) {
    const start = kept.lock.start;
    const end = kept.lock.end;
    return {
      text: next,
      passage: { start, end, text: next.slice(start, end) },
      rejected: false,
      changed: false,
    };
  }
  const keptText = lock.text ?? prev.slice(lock.start, lock.end);
  if (lockEdits) {
    return {
      text: prev,
      passage: { start: lock.start, end: lock.end, text: keptText },
      rejected: true,
      changed: false,
    };
  }
  if (markChanged) {
    // The passage itself was edited. Do not slide the span over the new words.
    return {
      text: next,
      passage: { start: lock.start, end: lock.end, text: keptText },
      rejected: false,
      changed: true,
    };
  }
  const needle = keptText;
  const at = needle ? next.indexOf(needle) : -1;
  const again = at >= 0 ? next.indexOf(needle, at + 1) : -1;
  if (at >= 0 && again < 0) {
    return {
      text: next,
      passage: { start: at, end: at + needle.length, text: needle },
      rejected: false,
      changed: false,
    };
  }
  const start = Math.max(0, Math.min(lock.start, next.length));
  const growth = next.length - prev.length;
  const end = Math.max(start, Math.min(lock.end + growth, next.length));
  return {
    text: next,
    passage: { start, end, text: next.slice(start, end) },
    rejected: false,
    changed: false,
  };
}

/** Pieces cover the whole string. kinds are selection, rule, and lock. */
export function passageMirrorPieces(text, selection, ruleRanges, lockRange) {
  const source = String(text || '');
  const marks = [];
  if (selection && selection.end > selection.start) {
    marks.push({ start: selection.start, end: selection.end, kind: 'selection' });
  }
  (ruleRanges || []).forEach((range) => {
    if (range && range.end > range.start) marks.push({ start: range.start, end: range.end, kind: 'rule' });
  });
  if (lockRange && lockRange.end > lockRange.start) {
    marks.push({ start: lockRange.start, end: lockRange.end, kind: 'lock' });
  }
  const points = new Set([0, source.length]);
  marks.forEach((mark) => {
    points.add(Math.max(0, Math.min(mark.start, source.length)));
    points.add(Math.max(0, Math.min(mark.end, source.length)));
  });
  const sorted = [...points].sort((a, b) => a - b);
  const pieces = [];
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (end <= start) continue;
    const kinds = marks
      .filter((mark) => mark.start <= start && mark.end >= end)
      .map((mark) => mark.kind);
    pieces.push({ start, end, text: source.slice(start, end), kinds });
  }
  if (!pieces.length) pieces.push({ start: 0, end: source.length, text: source, kinds: [] });
  return pieces;
}

function finiteOffset(value) {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Confirm the draft slice at the stored span equals selected_text exactly.
 * The server trims edge spaces, then widens a part-word, and those offsets
 * (after any shift for edits above the passage) are the span to replace
 * when the slice still matches. If it does not, nothing should be applied.
 * With no response span, the posted selection is used only when that slice
 * is still the posted text. status is 'response', 'posted', or 'changed'.
 */
export function rewriteReplacementSpan(draft, fallback, response) {
  const source = String(draft || '');
  const fbStart = Math.max(0, Math.min(fallback?.start ?? 0, source.length));
  const fbEnd = Math.max(fbStart, Math.min(fallback?.end ?? fbStart, source.length));
  const fallbackText = fallback?.text ?? source.slice(fbStart, fbEnd);
  const start = finiteOffset(response?.selection_start ?? response?.selectionStart);
  const end = finiteOffset(response?.selection_end ?? response?.selectionEnd);
  const selected = response?.selected_text ?? response?.selectedText;
  if (start != null && end != null && typeof selected === 'string') {
    const matches = start >= 0
      && end >= start
      && end <= source.length
      && source.slice(start, end) === selected;
    return {
      start,
      end,
      text: selected,
      status: matches ? 'response' : 'changed',
    };
  }
  const postedMatches = source.slice(fbStart, fbEnd) === fallbackText;
  return {
    start: fbStart,
    end: fbEnd,
    text: fallbackText,
    status: postedMatches ? 'posted' : 'changed',
  };
}

function detailMentionsInstruction(detail) {
  if (detail == null) return false;
  if (typeof detail === 'string') return detail.toLowerCase().includes('instruction');
  if (Array.isArray(detail)) return detail.some(detailMentionsInstruction);
  if (typeof detail === 'object') {
    const loc = Array.isArray(detail.loc) ? detail.loc.join(' ') : String(detail.loc || '');
    const field = String(detail.field || detail.code || '');
    const msg = String(detail.msg || detail.message || '');
    if (`${loc} ${field}`.toLowerCase().includes('instruction')) return true;
    return detailMentionsInstruction(msg);
  }
  return false;
}

/** A 422 instruction_too_long is a field error, not a failed rewrite. */
export function instructionTooLong(err) {
  if (Number(err?.status) !== 422) return false;
  const code = err?.detail?.code || err?.code || '';
  if (code === 'instruction_too_long') return true;
  if (detailMentionsInstruction(err?.detail)) return true;
  return String(err?.message || '').toLowerCase().includes('instruction');
}

export function passageErrorKind(err) {
  const status = Number(err?.status) || 0;
  const code = err?.detail?.code || err?.code || '';
  if (status === 402 || code === 'insufficient_credits' || code === 'credits_error') return 'credits';
  if (status === 409 && code === 'passage_changed') return 'passage_changed';
  if (instructionTooLong(err)) return 'instruction';
  return 'error';
}

/** A 5xx may have run the model. A rejection, a timeout, or no response did not. */
export function passageCallDidNotRun(err) {
  const kind = passageErrorKind(err);
  if (kind === 'credits' || kind === 'passage_changed') return true;
  const status = Number(err?.status) || 0;
  if (!status || status === 408) return true;
  if (status >= 500) return false;
  return status >= 400 && status < 500;
}

function readTextZoom() {
  if (typeof document === 'undefined') return 1;
  const raw = document.documentElement?.style?.getPropertyValue('--zoom-level') || '';
  const zoom = Number.parseFloat(raw);
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
}

export function visibleDraftBesidePanel(editor, width = typeof window === 'undefined' ? 0 : window.innerWidth) {
  if (!editor || !width) return null;
  const rect = editor.getBoundingClientRect?.();
  if (!rect || rect.width < 8) return null;
  const panelWidth = Math.min(PANEL_WIDTH_PX, Math.max(0, width - PANEL_EDGE_PX * 2));
  const panelLeft = width - PANEL_EDGE_PX - panelWidth;
  return panelLeft - rect.left;
}

/** Allocated width of the draft column, not the viewport. */
export function passageColumnWidth(editor) {
  if (!editor) return null;
  const column = editor.closest?.('.sw-prose-field, .sw-writing') || editor;
  const client = Number(column.clientWidth) || 0;
  if (client >= 8) return client;
  const width = Number(column.getBoundingClientRect?.().width) || 0;
  return width >= 8 ? width : null;
}

export function readPassageLayout(options = {}) {
  const width = options.width ?? (typeof window === 'undefined' ? 1024 : (window.innerWidth || 1024));
  const pinch = options.pinch ?? (typeof window === 'undefined' ? 1 : (window.visualViewport?.scale || 1));
  const screenWidth = options.screenWidth ?? (typeof window === 'undefined' ? 0 : window.screen?.width);
  const textZoom = options.textZoom ?? readTextZoom();
  const visibleDraft = options.visibleDraft;
  const columnWidth = options.columnWidth;
  let zoom = Math.max(pinch || 1, textZoom || 1);
  if (screenWidth && width && screenWidth / width >= 1.75 && width >= 480) {
    zoom = Math.max(zoom, screenWidth / width);
  }
  if (width <= FULL_PAGE_MAX_PX) return 'full';
  // A wide page at 200% can still leave a ~130px draft column beside Scene
  // context. Measure that column before the viewport zoom shortcut.
  if (typeof columnWidth === 'number' && columnWidth < BLEED_BELOW_PX) return 'bleed';
  if (zoom >= 1.75) return 'stacked';
  if (width <= PHONE_MAX_PX) return 'phone';
  if (width < STACK_BELOW_PX) return 'stacked';
  if (typeof columnWidth === 'number' && columnWidth < COLUMN_STACK_BELOW_PX) return 'stacked';
  if (typeof visibleDraft === 'number' && visibleDraft < DRAFT_MIN_BESIDE_PANEL) return 'stacked';
  return 'desktop';
}

export function shortcutLabel() {
  if (typeof navigator === 'undefined') return REWRITE_PASSAGE_SHORTCUT_WIN;
  const platform = `${navigator.platform || ''} ${navigator.userAgent || ''}`;
  if (/Mac|iPhone|iPad|iPod/.test(platform)) return REWRITE_PASSAGE_SHORTCUT_MAC;
  return REWRITE_PASSAGE_SHORTCUT_WIN;
}
